/**
 * New-release detection for the soft "new version available" banner.
 *
 * The running bundle bakes its release id into `__APP_BUILD_ID__`. The deployed
 * `version.json` (served `no-cache` from the app origin) always reflects the
 * latest release. Comparing the two tells an already-open tab that a newer
 * version exists — without ever forcing a reload (durable assets on R2 keep the
 * old tab working until the user chooses to refresh).
 */

export const VERSION_ENDPOINT = '/version.json';

/** Build id compiled into this bundle. Empty string in unexpected setups. */
export function getCurrentBuildId(): string {
  return typeof __APP_BUILD_ID__ === 'string' ? __APP_BUILD_ID__ : '';
}

/** When this bundle was built, ISO-8601. Empty string in unexpected setups. */
export function getCurrentBuildTime(): string {
  return typeof __APP_BUILD_TIME__ === 'string' ? __APP_BUILD_TIME__ : '';
}

/**
 * Human-readable release label for analytics, e.g. `2026-09-08-abb9311`.
 *
 * Amplitude exposes a single `appVersion`, so the date leads: it sorts
 * chronologically as a plain string, which makes "who is still on an old
 * client" readable straight off the Version breakdown without resolving a sha
 * by hand. The short sha follows to tell same-day builds apart and to point
 * back at a commit.
 *
 * Undefined when no build id was baked in (dev server, misconfig) — better an
 * absent version than a meaningless one.
 */
export function getAppVersionLabel(): string | undefined {
  const buildId = getCurrentBuildId();
  if (!buildId) {
    return undefined;
  }

  const shortId = buildId.slice(0, 7);
  const buildTime = getCurrentBuildTime();
  const builtAt = buildTime ? new Date(buildTime) : null;
  if (!builtAt || Number.isNaN(builtAt.getTime())) {
    return shortId;
  }

  // Date only, in UTC: the calendar day a release shipped is the granularity
  // this question is asked at, and it stays stable whatever the viewer's zone.
  return `${builtAt.toISOString().slice(0, 10)}-${shortId}`;
}

/**
 * True when `remote` denotes a different release than the one running, and both
 * ids are usable. A missing/empty current id (dev, misconfig) never prompts.
 */
export function isNewerBuild(current: string, remote: string | null): boolean {
  if (!current || !remote) {
    return false;
  }
  return remote !== current;
}

/** Extracts the build id from a parsed version.json payload, if present. */
export function parseBuildId(payload: unknown): string | null {
  if (
    typeof payload === 'object' &&
    payload !== null &&
    'buildId' in payload &&
    typeof (payload as { buildId: unknown }).buildId === 'string'
  ) {
    const buildId = (payload as { buildId: string }).buildId;
    return buildId.length > 0 ? buildId : null;
  }
  return null;
}

/**
 * Fetches the deployed build id. Returns null on any failure (offline, 404 in
 * dev, malformed payload) — callers treat null as "no update info available".
 */
export async function fetchRemoteBuildId(
  signal?: AbortSignal,
): Promise<string | null> {
  try {
    const response = await fetch(VERSION_ENDPOINT, {
      cache: 'no-store',
      signal,
    });
    if (!response.ok) {
      return null;
    }
    const payload = (await response.json()) as unknown;
    return parseBuildId(payload);
  } catch {
    return null;
  }
}
